import { Controller, useForm } from 'react-hook-form'
import { useEffect, type FC } from 'react'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Text } from '@wartek-id/text-v1'
import { Select } from '@wartek-id/select'
import { toast } from '@wartek-id/toast-v2'

import {
  PENETAPAN_PREDIKAT_KINERJA,
  PENETAPAN_PREDIKAT_KINERJA_RATINGS_PERILAKU_KERJA,
  PENETAPAN_PREDIKAT_KINERJA_RATINGS_PRAKTIK_KINERJA,
} from 'app/KinerjaShared/constants/penetapanPredikatKinerja'
import {
  API_GET_KS_PENILAIAN_BY_DINAS_QUERY_KEY,
  useApiPatchPenilaianDinas,
} from 'api/kinerja/kepala-sekolah/dinas/penilaian'
import {
  API_GET_KS_PENILAIAN_BY_DINAS_TAB_GURU_QUERY_KEY,
  useApiPatchPenilaianDinasTabGuru,
} from 'api/kinerja/kepala-sekolah/guru/dinas/penilaian'

import type { TabFilterRoles } from 'types/kinerja-ks'
import type {
  TPenetapanRatingPerilakuKerja,
  TPenetapanRatingPraktikKinerja,
} from 'types/kinerja-ks-pelaksanaan'
import type { TPenilaian } from 'types/kinerja-dinas'

type DialogPredikatKinerjaUpdateProps = {
  isOpen?: boolean
  onClose?: () => void
  data: TPenilaian | null
  activeTabName: TabFilterRoles
}

type FormInput = {
  ratingPraktikKinerja: TPenetapanRatingPraktikKinerja | null
  ratingPerilakuKerja: TPenetapanRatingPerilakuKerja | null
}

const DialogPredikatKinerjaUpdate: FC<DialogPredikatKinerjaUpdateProps> = ({
  isOpen,
  onClose,
  data,
  activeTabName,
}) => {
  const queryClient = useQueryClient()

  const form = useForm<FormInput>({
    mode: 'onChange',
    defaultValues: {
      ratingPraktikKinerja: null,
      ratingPerilakuKerja: null,
    },
  })

  const { watch, control, reset } = form

  const watchAllFields = watch()

  useEffect(() => {
    if (data) {
      reset({
        ratingPraktikKinerja: data.ratingPraktikKinerja,
        ratingPerilakuKerja: data.ratingPerilakuKerja,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const { mutate: mutateKs, isLoading: isLoadingKs } =
    useApiPatchPenilaianDinas()
  const { mutate: mutateGuru, isLoading: isLoadingGuru } =
    useApiPatchPenilaianDinasTabGuru()

  const isMutating = isLoadingKs || isLoadingGuru

  const onSubmit = (values: FormInput) => {
    const payload = {
      id: data?.id,
      ratingPraktikKinerja: values?.ratingPraktikKinerja,
      ratingPerilakuKerja: values?.ratingPerilakuKerja,
    }

    const opts = {
      onError: () => {
        // Show error toast
        toast(
          `Gagal ubah Rekomendasi Predikat Kinerja ${data?.name}. Coba lagi.`
        )
      },
      onSuccess: () => {
        // Show toast
        toast.success(
          `Berhasil ubah Rekomendasi Predikat Kinerja<br />${data?.name}!`
        )

        // Close
        onClose()
      },
    }

    // Mutate
    if (activeTabName === 'GURU')
      mutateGuru(payload, {
        ...opts,
        onSuccess: () => {
          queryClient.invalidateQueries(
            API_GET_KS_PENILAIAN_BY_DINAS_TAB_GURU_QUERY_KEY
          )
          opts.onSuccess()
        },
      })
    else
      mutateKs(payload, {
        ...opts,
        onSuccess: () => {
          queryClient.invalidateQueries(API_GET_KS_PENILAIAN_BY_DINAS_QUERY_KEY)
          opts.onSuccess()
        },
      })
  }

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        type="modal"
        autoFocus={false}
        closeOnOverlayClick={false}
      >
        <DialogOverlay />
        <DialogContent>
          <DialogHeader title="Ubah Rekomendasi Predikat Kinerja" />
          <div className="p-6 space-y-4">
            <Text color="subdued">
              Rekomendasi akan otomatis berubah berdasarkan Rating Praktik
              Kinerja dan Rating Perilaku Kerja. Mohon pertimbangkan hal-hal
              berikut ini:
            </Text>

            <fieldset className="space-y-1">
              <Text as="label" htmlFor="reason">
                Rating Praktik Kinerja
              </Text>
              <Controller
                control={control}
                rules={{ required: true }}
                name="ratingPraktikKinerja"
                render={({ field: { onChange, value } }) => (
                  <Select
                    className="whitespace-nowrap"
                    id="updated"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={isMutating}
                  >
                    {PENETAPAN_PREDIKAT_KINERJA_RATINGS_PRAKTIK_KINERJA.map(
                      (value) => (
                        <option
                          value={value}
                          key={value}
                          selected={value === data?.ratingPraktikKinerja}
                        >
                          {value}
                        </option>
                      )
                    )}
                  </Select>
                )}
              />
            </fieldset>

            <fieldset className="space-y-1">
              <Text as="label" htmlFor="reason">
                Rating Perilaku Kerja
              </Text>
              <Controller
                control={control}
                rules={{ required: true }}
                name="ratingPerilakuKerja"
                render={({ field: { onChange, value } }) => (
                  <Select
                    className="whitespace-nowrap"
                    id="updated"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={isMutating}
                  >
                    {PENETAPAN_PREDIKAT_KINERJA_RATINGS_PERILAKU_KERJA.map(
                      (value) => (
                        <option
                          value={value}
                          key={value}
                          selected={value === data?.ratingPerilakuKerja}
                        >
                          {value}
                        </option>
                      )
                    )}
                  </Select>
                )}
              />
            </fieldset>

            <div className="border border-subdued rounded bg-gray-5 flex items-center justify-between gap-4 px-6 py-4">
              <Text variant="body" className="text-gray-90">
                Poin Pengembangan Kompetensi
              </Text>
              <Text variant="subheading" color="subdued" className="uppercase">
                {data?.poinPengembanganKompetensi || 0} Poin
              </Text>
            </div>

            <fieldset className="space-y-1">
              <Text as="label" htmlFor="reason">
                Rekomendasi Predikat Kinerja:
              </Text>
              <div className="grid grid-cols-3 gap-4 border border-gray-70 rounded">
                <div className="col-span-2 bg-white px-6 py-4 rounded-l-md">
                  <Text variant="heading-sm" className="text-black truncate">
                    {data?.name}
                  </Text>
                </div>
                <div className="bg-gray-90 px-6 py-4">
                  <Text variant="heading-sm" className=" !text-white">
                    {
                      PENETAPAN_PREDIKAT_KINERJA?.[
                        watchAllFields.ratingPraktikKinerja ||
                          data?.ratingPraktikKinerja
                      ]?.[
                        watchAllFields.ratingPerilakuKerja ||
                          data?.ratingPerilakuKerja
                      ]
                    }
                  </Text>
                </div>
              </div>
            </fieldset>
          </div>
          <DialogFooter className="space-x-4">
            <Button
              color="white"
              size="lg"
              disabled={isMutating}
              onClick={onClose}
            >
              Batal
            </Button>
            <Button
              color="blue"
              size="lg"
              disabled={isMutating || !form.formState.isDirty}
              onClick={() => {
                onSubmit(form.getValues())
              }}
            >
              Lanjut
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default DialogPredikatKinerjaUpdate
